import api from '@/api';
import { useAppStore } from '@/store';
import { i18n } from '@/i18n';
import { LINK_TYPE } from '@/constants';

const t = i18n.global.t;
function formatDate(dateString) {
  if (!dateString) return '';
  return dateString.split(' ')[0];
}
function formattedHour(dateparam) {
  const date = new Date(dateparam);
  return `${date.getHours()}:${date.getMinutes()}`;
}
function formatNumber(number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2, // Adjust based on the precision you need
    useGrouping: true,
  })
    .format(number)
    .replace(/,/g, ' ');
}

async function updateLastSeen(place) {
  try {
    const now = new Date();
    let lastSeen = JSON.parse(localStorage.getItem('lastSeen')) || {
      publications: now,
      exhibition_stores: now,
      exhibition_products: now,
      mall_stores: now,
      mall_products: now,
      videos: now,
      announcements: now,
      business_accounts: now,
      payments: now,
      published_publications: now,
      rejected_publications: now,
    };

    lastSeen[place] = now;

    localStorage.setItem('lastSeen', JSON.stringify(lastSeen));

    const response = await api.post('last_seen/', lastSeen);
    useAppStore().setState('lastSeen', response.data);
  } catch (error) {
    console.error(error);
  }
}

function isVideo(el) {
  if (el) return ['.mp4', '.mov', '.avi', '.mkv', '.webm'].some(ext => el.includes(ext));
}

function getDay(work_hours) {
  if (work_hours) {
    let date = new Date();
    return date.toLocaleString('en-us', { weekday: 'long' });
  }
  return null;
}

function isTodayDate(dateString) {
  const inputDate = new Date(dateString.replace(/\./g, '/'));
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (
    inputDate.getDate() === today.getDate() &&
    inputDate.getMonth() === today.getMonth() &&
    inputDate.getFullYear() === today.getFullYear()
  ) {
    const hours = inputDate.getHours().toString().padStart(2, '0');
    const minutes = inputDate.getMinutes().toString().padStart(2, '0');
    return `${t('today')}, ${hours}:${minutes}`;
  } else if (
    inputDate.getDate() === yesterday.getDate() &&
    inputDate.getMonth() === yesterday.getMonth() &&
    inputDate.getFullYear() === yesterday.getFullYear()
  ) {
    const hours = inputDate.getHours().toString().padStart(2, '0');
    const minutes = inputDate.getMinutes().toString().padStart(2, '0');
    return `${t('yesterday')}, ${hours}:${minutes}`;
  }
  return dateString;
}

function appendSpinner(element) {
  const dropdownList = element;
  if (dropdownList && !dropdownList.querySelector('.loading-spinner')) {
    const spinnerContainer = document.createElement('li');
    spinnerContainer.classList.add(
      'loading-spinner',
      'flex',
      'items-center',
      'justify-center',
      'p-2',
    );
    spinnerContainer.innerHTML = `
            <svg class="w-4 h-4 mr-2 text-gray-200 animate-spin fill-main-color" aria-hidden="true"
                viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor" />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill" />
            </svg>
        `;
    dropdownList.appendChild(spinnerContainer);
  }
}
function removeSpinner(element) {
  const dropdownList = element;
  if (dropdownList) {
    const spinner = dropdownList.querySelector('.loading-spinner');
    if (spinner) {
      dropdownList.removeChild(spinner);
    }
  }
}

const getExternalLink = link => {
  if (!link) return '/';

  const normalizedLink = String(link).trim();

  if (
    normalizedLink.startsWith('http://') ||
    normalizedLink.startsWith('https://') ||
    normalizedLink.startsWith('mailto:') ||
    normalizedLink.startsWith('tel:')
  ) {
    return normalizedLink;
  }

  if (normalizedLink.startsWith('//')) {
    return `https:${normalizedLink}`;
  }

  if (normalizedLink.startsWith('/')) {
    return normalizedLink;
  }

  return `https://${normalizedLink}`;
};

const hasAdvertisementLink = data => {
  const linkType = data?.link_type;
  const link = String(data?.link ?? '').trim();

  if (linkType === null || linkType === undefined || !link) {
    return false;
  }

  const normalizedType = Number(linkType);
  return normalizedType === 0 || Boolean(LINK_TYPE[normalizedType]?.route);
};

const isExternalAdvertisementLink = data =>
  hasAdvertisementLink(data) && Number(data?.link_type) === 0;

const getRouteParams = data => {
  let params = {
    id: data?.link,
  };
  let query = {};
  let name = LINK_TYPE[data?.link_type]?.route;
  if (LINK_TYPE[data?.link_type]?.hasOptions) {
    query['category'] = data?.link;
  }
  return {
    name: name,
    params: params,
    query: query,
  };
};

export {
  formatDate,
  updateLastSeen,
  formattedHour,
  isVideo,
  formatNumber,
  getDay,
  isTodayDate,
  appendSpinner,
  removeSpinner,
  getRouteParams,
  getExternalLink,
  hasAdvertisementLink,
  isExternalAdvertisementLink,
};
